import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from '@conexasaude/conexa-hero'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  useGetCollectionAttemptsOrOutcome,
  useGetGenerateNote,
  useGetServiceDetailsList,
} from '@/modules/Users/queries/PatientQueries/Patient.queries'
import { useModalStore } from '@/store/useModal'
import usePatientStore from '@/modules/Users/store/Patient/PatientStore'
import {
  DEFAULT_CANCEL_REASON,
  OTHERS_CANCEL_CATEGORY,
  ServiceDetailsModal,
} from './ServiceDetails.constants'

export const useServiceDetails = () => {
  const { showModal, closeModal, modalOpen } = useModalStore()
  const matches = useMediaQuery('(max-width: 1366px)')
  const navigate = useNavigate()
  const serviceId = useParams()
  const { updateValue } = usePatientStore()
  const location = useLocation()

  const isAppointmentHistoryRoute = useMemo(() => {
    return location.pathname.includes('/atendimento/historico-atendimentos')
  }, [location.pathname])

  const [paymentId, setPaymentId] = useState(null)
  const [buttonPressed, setButtonPressed] = useState(true)

  const { serviceDetailsList } = useGetServiceDetailsList(serviceId?.userId)

  const { collectionAttemptsList } = useGetCollectionAttemptsOrOutcome(
    serviceId?.userId,
  )

  const { generateNoteMutation } = useGetGenerateNote()

  const hasPayment = useMemo(() => {
    const situationPayment =
      serviceDetailsList?.patientPayment?.situationPayment

    return Boolean(
      situationPayment === 'Reservado' || situationPayment === 'Pago',
    )
  }, [serviceDetailsList?.patientPayment?.situationPayment])

  const hasNote = useMemo(() => {
    const hasNote = serviceDetailsList?.linkNFe

    return Boolean(hasNote)
  }, [serviceDetailsList?.linkNFe])

  const hasCancelCategory = useMemo(() => {
    return Boolean(serviceDetailsList?.cancelCategory)
  }, [serviceDetailsList?.cancelCategory])

  const cancelCategoryLabel = useMemo(() => {
    const { cancelCategory, cancelReason } = serviceDetailsList ?? {}
    const isOthers = cancelCategory === OTHERS_CANCEL_CATEGORY
    const hasReason =
      cancelReason && (isOthers || cancelReason !== DEFAULT_CANCEL_REASON)

    return hasReason ? `${cancelCategory} - ${cancelReason}` : cancelCategory
  }, [serviceDetailsList])

  const hasRefund = useMemo(() => {
    return Boolean(serviceDetailsList?.patientPayment?.refundUserId)
  }, [serviceDetailsList?.patientPayment?.refundUserId])

  const titleDetailsOrRefund = useMemo(() => {
    return hasPayment ? 'Estornar pagamento' : 'Detalhes do estorno'
  }, [hasPayment])

  const handleShowOptions = useMemo(() => {
    const status = serviceDetailsList?.status

    return {
      isCompleted: status === 'Concluido',
      isNotPending: status !== 'Pendente',
    }
  }, [serviceDetailsList?.status])

  const { isCompleted, isNotPending } = handleShowOptions

  const handleIconArrow = useCallback(() => {
    if (buttonPressed) {
      setButtonPressed(false)
    } else {
      setButtonPressed(true)
    }
  }, [buttonPressed])

  const handleHasPayment = useCallback(() => {
    return hasPayment
      ? ServiceDetailsModal.PAYMENT_REVERSE
      : ServiceDetailsModal.REFUND_DETAILS
  }, [hasPayment])

  const handleNote = useCallback(
    value => {
      const url = serviceDetailsList?.linkNFe
      if (value) {
        window.open(url, '_blank')
      } else {
        generateNoteMutation(paymentId)
      }
    },
    [generateNoteMutation, paymentId, serviceDetailsList?.linkNFe],
  )

  const redirectToReferralDetails = useCallback(
    (referral, serviceId) => {
      updateValue('patientId', referral?.patientId)
      updateValue('professionalId', referral?.professionalId)
      updateValue('attendanceId', serviceId?.userId)
      if (isAppointmentHistoryRoute) {
        navigate(
          `/atendimento/historico-atendimentos/detalhes-atendimento/detalhes-consulta/${referral?.callsId}`,
        )
      } else {
        navigate(
          `/usuario/detalhes-atendimento/detalhes-consulta/${referral?.callsId}`,
        )
      }
    },
    [isAppointmentHistoryRoute, navigate, updateValue],
  )

  useEffect(() => {
    if (serviceDetailsList?.patientPayment?.id) {
      setPaymentId(Number(serviceDetailsList?.patientPayment.id))
    }
  }, [serviceDetailsList])

  return {
    modalOpen,
    closeModal,
    redirectToReferralDetails,
    matches,
    serviceDetailsList,
    collectionAttemptsList,
    showModal,
    handleNote,
    hasNote,
    handleHasPayment,
    titleDetailsOrRefund,
    serviceId,
    hasPayment,
    isCompleted,
    isNotPending,
    hasRefund,
    hasCancelCategory,
    cancelCategoryLabel,
    handleIconArrow,
    buttonPressed,
    isAppointmentHistoryRoute,
  }
}
